import { useState } from 'react';
import {
  useCallToken,
  useCancelSession,
  useEndSession,
  useMessages,
  useMySessions,
} from '../../api/sessions';
import { Badge, SessionStatusBadge } from '../../components/Badge';
import { Avatar } from '../../components/Avatar';
import { ConfirmDialog } from '../../components/Modal';
import { EmptyState, ErrorState } from '../../components/EmptyState';
import { Spinner } from '../../components/Spinner';
import { useToast } from '../../components/Toast';
import { formatDateTime, formatRelative, formatSlotRange } from '../../lib/format';
import { ApiError } from '../../lib/api';
import type { Session, SessionScope, UserProfile } from '../../types/api';

/**
 * Read-only chat.
 *
 * Support can read a thread to understand a complaint, but cannot write
 * into it. A message sent from here would arrive looking exactly like the
 * account owner wrote it, with nothing to tell the other participant
 * otherwise — the API already refuses to let an admin post as themselves,
 * and routing around that through impersonation would be the same problem
 * wearing a disguise.
 */
function ThreadView({ session, viewerId }: { session: Session; viewerId: string }) {
  const messages = useMessages(session.id);

  if (messages.isLoading) {
    return (
      <div className="flex h-24 items-center justify-center text-ink-3">
        <Spinner />
      </div>
    );
  }
  if (messages.isError) {
    return (
      <p className="px-4 py-3 text-xs text-ink-3">
        {messages.error instanceof ApiError
          ? messages.error.message
          : 'Could not load this thread.'}
      </p>
    );
  }
  if (!messages.data || messages.data.length === 0) {
    return <p className="px-4 py-4 text-xs text-ink-3">No messages in this thread.</p>;
  }

  return (
    <div className="space-y-3 px-4 py-3">
      {messages.data.map((m) => {
        const mine = m.senderId === viewerId;
        return (
          <div key={m.id} className={`flex gap-2.5 ${mine ? 'flex-row-reverse' : ''}`}>
            <Avatar name={m.senderName} src={m.senderAvatarUrl} size="sm" />
            <div className={`max-w-[75%] ${mine ? 'text-right' : ''}`}>
              <div
                className={`inline-block rounded-lg px-3 py-2 text-[0.8125rem] ${
                  m.isDeleted
                    ? 'bg-surface-2 italic text-ink-3'
                    : mine
                      ? 'bg-accent-wash text-ink'
                      : 'bg-surface-2 text-ink'
                }`}
              >
                {m.isDeleted ? 'Message removed by a moderator' : m.body}
              </div>
              <div className="mt-0.5 text-[0.6875rem] text-ink-3">
                {m.senderName} · <span title={formatDateTime(m.sentAt)}>{formatRelative(m.sentAt)}</span>
                {m.readAt && ' · read'}
              </div>
            </div>
          </div>
        );
      })}
      <p className="border-t border-rule pt-2 text-center text-[0.6875rem] text-ink-3">
        Read only. Support cannot send messages as someone else.
      </p>
    </div>
  );
}

export function LessonsTab({ user }: { user: UserProfile }) {
  const { notify } = useToast();
  const [scope, setScope] = useState<SessionScope>('upcoming');
  const [expanded, setExpanded] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState<Session | null>(null);
  const [reason, setReason] = useState('');
  const [joinCheck, setJoinCheck] = useState<Record<string, string>>({});

  const sessions = useMySessions({ scope, limit: 50 });
  const cancelSession = useCancelSession();
  const endSession = useEndSession();
  const callToken = useCallToken();

  const checkJoin = async (session: Session) => {
    setJoinCheck((p) => ({ ...p, [session.id]: 'checking' }));
    try {
      const token = await callToken.mutateAsync(session.id);
      setJoinCheck((p) => ({
        ...p,
        [session.id]: `Can join — token valid until ${new Date(
          token.expiresAt * 1000,
        ).toLocaleTimeString()}`,
      }));
    } catch (err) {
      setJoinCheck((p) => ({
        ...p,
        [session.id]: err instanceof ApiError ? err.message : 'Could not get a token',
      }));
    }
  };

  const confirmCancel = async () => {
    if (!cancelling) return;
    try {
      await cancelSession.mutateAsync({ id: cancelling.id, reason: reason.trim() || undefined });
      notify('Lesson cancelled on their behalf', 'success');
      setCancelling(null);
      setReason('');
    } catch (err) {
      notify(err instanceof ApiError ? err.message : 'Could not cancel.', 'error');
    }
  };

  return (
    <div className="panel overflow-hidden">
      <div className="flex flex-wrap items-center gap-2 border-b border-rule px-3 py-2.5">
        <h3 className="text-sm font-semibold text-ink">Their lessons</h3>
        <select
          className="select ml-auto h-[2.125rem] w-auto"
          value={scope}
          aria-label="Time scope"
          onChange={(e) => setScope(e.target.value as SessionScope)}
        >
          <option value="upcoming">Upcoming</option>
          <option value="past">Past</option>
          <option value="all">All</option>
        </select>
      </div>

      {sessions.isLoading ? (
        <div className="flex h-32 items-center justify-center text-ink-3">
          <Spinner />
        </div>
      ) : sessions.isError ? (
        <ErrorState
          message={sessions.error instanceof ApiError ? sessions.error.message : undefined}
          onRetry={() => void sessions.refetch()}
        />
      ) : sessions.data && sessions.data.data.length > 0 ? (
        <ul className="divide-y divide-rule">
          {sessions.data.data.map((s) => {
            const other = user.role === 'tutor' ? s.student : s.tutor;
            const isOpen = expanded === s.id;
            const canAct = s.status === 'scheduled' || s.status === 'in_progress';

            return (
              <li key={s.id}>
                <div className="flex flex-wrap items-center gap-3 px-4 py-3">
                  <Avatar name={other.fullName} src={other.avatarUrl} />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-ink">{other.fullName}</span>
                      <SessionStatusBadge status={s.status} />
                      {s.subject && <Badge tone="accent">{s.subject}</Badge>}
                      {s.unreadCount > 0 && (
                        <Badge tone="live">{s.unreadCount} unread</Badge>
                      )}
                    </div>
                    <div className="mt-0.5 text-xs text-ink-3">
                      {formatSlotRange(s.startTime, s.endTime)}
                      {s.isJoinable && (
                        <span className="ml-2 text-live-text">· room is open</span>
                      )}
                    </div>
                    {joinCheck[s.id] && (
                      <div className="mt-1 text-[0.6875rem] text-ink-2">
                        {joinCheck[s.id] === 'checking' ? 'Checking…' : joinCheck[s.id]}
                      </div>
                    )}
                  </div>

                  <div className="flex flex-wrap items-center gap-1.5">
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => setExpanded(isOpen ? null : s.id)}
                    >
                      {isOpen ? 'Hide chat' : 'Read chat'}
                    </button>
                    {canAct && (
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => void checkJoin(s)}
                      >
                        Can they join?
                      </button>
                    )}
                    {s.status === 'in_progress' && (
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={async () => {
                          try {
                            await endSession.mutateAsync(s.id);
                            notify('Lesson marked finished', 'success');
                          } catch (err) {
                            notify(
                              err instanceof ApiError ? err.message : 'Could not end it.',
                              'error',
                            );
                          }
                        }}
                      >
                        Mark finished
                      </button>
                    )}
                    {canAct && (
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm text-danger"
                        onClick={() => setCancelling(s)}
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                </div>

                {isOpen && (
                  <div className="border-t border-rule bg-surface-2">
                    <ThreadView session={s} viewerId={user.id} />
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      ) : (
        <EmptyState
          title={scope === 'upcoming' ? 'Nothing coming up' : 'No lessons'}
          description={
            scope === 'upcoming'
              ? 'They have no lessons scheduled. Switch to "All" to see their history.'
              : 'This account has never booked a lesson.'
          }
        />
      )}

      <ConfirmDialog
        open={cancelling !== null}
        onClose={() => setCancelling(null)}
        onConfirm={confirmCancel}
        danger
        pending={cancelSession.isPending}
        title="Cancel on their behalf?"
        description="The other person is notified and the slot is freed. The cancellation is attributed to this account, not to you — your impersonation is in the audit log."
        confirmLabel="Cancel lesson"
        reason={reason}
        onReasonChange={setReason}
        reasonPlaceholder="Student asked support to cancel"
      />
    </div>
  );
}
